import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/customSupabaseClient';
import { Users, CreditCard, Activity, TrendingUp, Wallet, Database } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';

const AdminDashboard = () => {
    const [metrics, setMetrics] = useState({
        total_users: 0,
        active_trials: 0,
        active_subscriptions: 0,
        total_sales_value: 0,
        total_sales_qty: 0,
        total_receipts_value: 0,
        total_receipts_qty: 0,
        db_size_bytes: 0
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchMetrics = async () => {
            try {
                setLoading(true);
                const { data, error } = await supabase.rpc('admin_get_metrics');
                if (error) throw error;
                if (data) {
                    setMetrics(data);
                }
            } catch (err) {
                console.error("Erro ao carregar métricas do Admin:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchMetrics();
    }, []);

    const formatarMoeda = (valor) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2
        }).format(valor || 0);
    };

    const formatBytes = (bytes, decimals = 2) => {
        if (!+bytes) return '0 Bytes';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
    };

    // Calculate DB Usage percentage (assuming 500MB free tier limit)
    const dbLimitBytes = 500 * 1024 * 1024; // 500 MB
    const dbUsagePercent = Math.min((metrics.db_size_bytes / dbLimitBytes) * 100, 100);
    
    let dbColorClass = "bg-emerald-500";
    if (dbUsagePercent > 70) dbColorClass = "bg-amber-500";
    if (dbUsagePercent > 90) dbColorClass = "bg-red-500";

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-amber-200 to-amber-500">
                Visão Geral do Sistema
            </h1>
            <p className="text-slate-400">Aqui você acompanha a saúde, volume financeiro e infraestrutura do SaaS.</p>

            {/* Cards de Métricas */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
                
                {/* Usuários Totais */}
                <Card className="bg-slate-800 border-slate-700 shadow-xl">
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium text-slate-300">Total de Usuários</CardTitle>
                        <Users className="w-4 h-4 text-blue-400" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-3xl font-bold text-white">{metrics.total_users}</div>
                        <p className="text-xs text-slate-500 mt-1">Contas criadas</p>
                    </CardContent>
                </Card>

                {/* Testes Ativos (Trials) */}
                <Card className="bg-slate-800 border-slate-700 shadow-xl">
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium text-slate-300">Trials Ativos</CardTitle>
                        <Activity className="w-4 h-4 text-emerald-400" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-3xl font-bold text-white">{metrics.active_trials}</div>
                        <p className="text-xs text-slate-500 mt-1">Podem converter em vendas</p>
                    </CardContent>
                </Card>

                {/* Assinaturas Ativas */}
                <Card className="bg-slate-800 border-slate-700 shadow-xl relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-amber-400/20 to-transparent blur-xl"></div>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium text-slate-300">Assinaturas Ativas</CardTitle>
                        <CreditCard className="w-4 h-4 text-amber-400" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-3xl font-bold text-amber-400">{metrics.active_subscriptions}</div>
                        <p className="text-xs text-slate-500 mt-1">Clientes pagantes</p>
                    </CardContent>
                </Card>

                {/* Vendas Processadas */}
                <Card className="bg-slate-800 border-slate-700 shadow-xl">
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium text-slate-300">Total de Vendas</CardTitle>
                        <TrendingUp className="w-4 h-4 text-indigo-400" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold text-white truncate" title={formatarMoeda(metrics.total_sales_value)}>
                            {formatarMoeda(metrics.total_sales_value)}
                        </div>
                        <p className="text-xs text-slate-500 mt-1">{metrics.total_sales_qty.toLocaleString('pt-BR')} registros importados</p>
                    </CardContent>
                </Card>

                {/* Recebimentos Processados */}
                <Card className="bg-slate-800 border-slate-700 shadow-xl">
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium text-slate-300">Total de Recebimentos</CardTitle>
                        <Wallet className="w-4 h-4 text-emerald-400" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold text-white truncate" title={formatarMoeda(metrics.total_receipts_value)}>
                            {formatarMoeda(metrics.total_receipts_value)}
                        </div>
                        <p className="text-xs text-slate-500 mt-1">{metrics.total_receipts_qty.toLocaleString('pt-BR')} registros liquidados</p>
                    </CardContent>
                </Card>

                {/* Uso de Armazenamento */}
                <Card className="bg-slate-800 border-slate-700 shadow-xl">
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium text-slate-300">Armazenamento Supabase</CardTitle>
                        <Database className="w-4 h-4 text-slate-400" />
                    </CardHeader>
                    <CardContent>
                        <div className="flex justify-between items-baseline mb-2">
                            <span className="text-2xl font-bold text-white">{formatBytes(metrics.db_size_bytes)}</span>
                            <span className="text-xs text-slate-400">/ 500 MB</span>
                        </div>
                        <div className="w-full bg-slate-700 rounded-full h-2">
                            <div className={`h-2 rounded-full ${dbColorClass} transition-all duration-500`} style={{ width: `${dbUsagePercent}%` }}></div>
                        </div>
                        <p className="text-[10px] text-slate-500 mt-2 text-right">Peso real no BD Postgres</p>
                    </CardContent>
                </Card>

            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                 {/* Espaço para gráficos ou logs de sistema futuros */}
                 <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 min-h-[300px] flex items-center justify-center">
                    <p className="text-slate-500 text-sm">Visualizações Avançadas chegarão em breve.</p>
                 </div>
                 <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 min-h-[300px] flex items-center justify-center">
                    <p className="text-slate-500 text-sm">Registro de Atividades chegará em breve.</p>
                 </div>
            </div>

        </div>
    );
};

export default AdminDashboard;
